import { buildCheckoutPrefillParams } from "@/lib/checkoutUrl";
import { getCartIdentityFingerprint } from "@/lib/cartIdentity";

const CART_STORAGE_KEY = "so-buy-again-cart";

/** Customer info for checkout prefill - matches Customer from usePurchasedProductDetail */
export interface CartCustomer {
  email?: string;
  shipping_address?: {
    first_name?: string;
    last_name?: string;
    address1?: string;
    address2?: string | null;
    city?: string;
    country?: string;
    province?: string;
    zip?: string;
    phone?: string | null;
  } | null;
}

export interface CartItemMeta {
  title?: string;
  image_url?: string;
  variantTitle?: string;
  shopDomain?: string;
  customer?: CartCustomer;
}

export interface CartItem {
  variantId: number;
  quantity: number;
  /** Our API product id - used to enforce one variant per product and show variant in panel */
  productId?: number;
  price?: number;
  /** Original price before discount (for strikethrough display) */
  originalPrice?: number;
  title?: string;
  image_url?: string;
  variantTitle?: string;
  sellingPlanId?: string;
  /** Admin-overridden save badge percent (when set, takes priority over computed value) */
  badgePercent?: number;
}

export interface CartData {
  items: CartItem[];
  shopDomain?: string;
  customer?: CartCustomer;
  cartIdentity?: string;
}

function getCartKey(): string {
  if (typeof window === "undefined") return CART_STORAGE_KEY;
  const shop = (window as unknown as { Shopify?: { shop?: string } })?.Shopify
    ?.shop;
  return shop ? `${CART_STORAGE_KEY}_${shop}` : CART_STORAGE_KEY;
}

function readCart(): CartData {
  if (typeof window === "undefined") return { items: [] };
  try {
    const raw = window.localStorage.getItem(getCartKey());
    if (!raw) return { items: [] };
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed &&
      typeof parsed === "object" &&
      Array.isArray((parsed as CartData).items)
    ) {
      const data = parsed as CartData;
      const items = data.items.filter(
        (i): i is CartItem =>
          i != null &&
          typeof i === "object" &&
          typeof (i as CartItem).variantId === "number" &&
          typeof (i as CartItem).quantity === "number",
      );

      const current = getCartIdentityFingerprint();
      if (items.length > 0) {
        if (data.cartIdentity != null && data.cartIdentity !== current) {
          writeCart({ items: [] });
          return { items: [] };
        }
        if (data.cartIdentity == null) {
          writeCart({
            items,
            shopDomain: data.shopDomain,
            customer: data.customer,
          });
        }
      }

      return {
        shopDomain: data.shopDomain,
        customer: data.customer,
        items,
      };
    }
  } catch {
    // ignore parse errors
  }
  return { items: [] };
}

function writeCart(data: CartData): void {
  if (typeof window === "undefined") return;
  try {
    const payload: CartData = {
      ...data,
      cartIdentity: getCartIdentityFingerprint(),
    };
    window.localStorage.setItem(getCartKey(), JSON.stringify(payload));
  } catch {
    // ignore write errors
  }
}

export function getCartItems(): CartItem[] {
  return readCart().items;
}

export function getCartQuantity(variantId: number): number {
  const item = readCart().items.find((i) => i.variantId === variantId);
  return item?.quantity ?? 0;
}

export function isInCart(variantId: number): boolean {
  return getCartQuantity(variantId) > 0;
}

export interface AddToCartMeta {
  /** Our API product id - enforces one variant per product in cart */
  productId?: number;
  title?: string;
  image_url?: string;
  variantTitle?: string;
  price?: number;
  /** Original price before discount (for strikethrough display) */
  originalPrice?: number;
  shopDomain?: string;
  sellingPlanId?: string;
  customer?: CartCustomer;
  badgePercent?: number;
}

export function addToCart(
  variantId: number,
  quantity: number,
  meta?: AddToCartMeta,
): CartItem[] {
  const cart = readCart();
  // Enforce one variant per product: remove any existing variant of same product
  if (meta?.productId != null) {
    cart.items = cart.items.filter((i) => i.productId !== meta.productId);
  }
  const existing = cart.items.findIndex((i) => i.variantId === variantId);
  const newQty = Math.max(1, quantity);
  if (existing >= 0) {
    cart.items[existing]!.quantity += newQty;
  } else {
    const item: CartItem = { variantId, quantity: newQty };
    if (meta?.productId != null) item.productId = meta.productId;
    if (meta?.title) item.title = meta.title;
    if (meta?.image_url) item.image_url = meta.image_url;
    if (meta?.variantTitle) item.variantTitle = meta.variantTitle;
    if (meta?.price != null) item.price = meta.price;
    if (meta?.originalPrice != null) item.originalPrice = meta.originalPrice;
    if (meta?.sellingPlanId) item.sellingPlanId = meta.sellingPlanId;
    if (meta?.badgePercent != null) item.badgePercent = meta.badgePercent;
    cart.items.push(item);
  }
  if (meta?.shopDomain) {
    cart.shopDomain = meta.shopDomain;
  }
  if (meta?.customer) {
    cart.customer = meta.customer;
  }
  writeCart(cart);
  return cart.items;
}

export function removeFromCart(variantId: number): CartItem[] {
  const cart = readCart();
  cart.items = cart.items.filter((i) => i.variantId !== variantId);
  writeCart(cart);
  return cart.items;
}

export function setCartQuantity(
  variantId: number,
  quantity: number,
): CartItem[] {
  if (quantity <= 0) {
    return removeFromCart(variantId);
  }
  const cart = readCart();
  const existing = cart.items.findIndex((i) => i.variantId === variantId);
  if (existing >= 0) {
    cart.items[existing]!.quantity = quantity;
  } else {
    cart.items.push({ variantId, quantity });
  }
  writeCart(cart);
  return cart.items;
}

/** Derives myshopify domain from shop_domain */
function toMyshopifyDomain(shopDomain: string): string {
  const cleaned = shopDomain.replace(/^https?:\/\//, "").split("/")[0] ?? "";
  if (cleaned.includes(".myshopify.com")) return cleaned;
  const withoutWww = cleaned.replace(/^www\./, "");
  const handle = withoutWww.split(".")[0] ?? withoutWww;
  return `${handle}.myshopify.com`;
}

/**
 * Builds custom checkout URL: /cart/clear?return_to=/cart/add?items...
 * Each item: items[][id], items[][quantity], items[][selling_plan] (optional)
 */
export function getCheckoutUrl(discountCode?: string | null): string {
  const cart = readCart();
  if (cart.items.length === 0) return "";
  const rawDomain =
    cart.shopDomain ??
    (typeof window !== "undefined" ? window.location.hostname : "");
  if (!rawDomain) return "";
  const shopDomain = toMyshopifyDomain(rawDomain.replace(/^https?:\/\//, ""));

  const addParams = new URLSearchParams();
  for (const item of cart.items) {
    addParams.append("items[][id]", String(item.variantId));
    addParams.append("items[][quantity]", String(item.quantity));
    if (item.sellingPlanId) {
      addParams.append("items[][selling_plan]", item.sellingPlanId);
    }
  }
  const prefillParams = cart.customer
    ? buildCheckoutPrefillParams(
        cart.customer as Parameters<typeof buildCheckoutPrefillParams>[0],
      )
    : new URLSearchParams();
  if (discountCode) {
    prefillParams.append("discount", discountCode);
  }
  const checkoutPath =
    prefillParams.toString() !== ""
      ? `/checkout?${prefillParams.toString()}`
      : "/checkout";
  addParams.append("attributes[social_ocean_source]", "the-widget");
  addParams.append("return_to", checkoutPath);
  const addPath = `/cart/add?${addParams.toString()}`;
  const returnTo = encodeURIComponent(addPath);
  return `https://${shopDomain}/cart/clear?return_to=${returnTo}`;
}
